import { FC } from "react";
import { CalendarDays } from "lucide-react";
import moment from "moment";
import { useGetCardSubscription } from "@/hooks/use-queries";

const statusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "active":
      return { color: "#4ADE80", background: "rgba(74, 222, 128, 0.12)" };
    case "cancelled":
    case "canceled":
    case "expired":
      return { color: "#F87171", background: "rgba(248, 113, 113, 0.12)" };
    default:
      return { color: "#FACC15", background: "rgba(250, 204, 21, 0.12)" };
  }
};

type Props = { cardId?: string; subscription?: CardSubscription };

const isCancelledStatus = (status?: string | null) =>
  ["cancelled", "canceled"].includes(status?.toLowerCase() ?? "");

const SubscriptionStatusCard: FC<Props> = ({ cardId, subscription }) => {
  const cancelled = isCancelledStatus(subscription?.status);
  const expired = subscription?.status?.toLowerCase() === "expired";
  const { data: live } = useGetCardSubscription({
    id: cardId,
    enabled: cancelled,
  });

  if (subscription?.billingType !== "subscription") return null;

  const { status, isDefaultCardHolder } = subscription;
  // A cancelled plan shows when it was last active (last charge) instead of a
  // renewal date.
  const nextBillingDate = cancelled
    ? ((live?.subscribed ? live.lastChargedAt : null) ??
      subscription.nextBillingDate)
    : subscription.nextBillingDate;
  const pkg = subscription.package;
  // Default-tab cards bill the package's defaultPrice, Custom-tab cards its price.
  const price = pkg ? (isDefaultCardHolder ? pkg.defaultPrice : pkg.price) : null;
  const period = pkg?.durationMonths === 12 ? "year" : "month";

  return (
    <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#EBE8F308] p-4 text-white">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/5">
          <CalendarDays className="size-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-white/80">
            {cancelled ? "Last active" : expired ? "Expired on" : "Next renewal"}
          </p>
          <p className="text-base font-semibold">
            {nextBillingDate ? moment(nextBillingDate).format("MMM D, YYYY") : "—"}
          </p>
          {price != null && (
            <p className="text-[11px] text-white/60">
              ${price} / {period}
            </p>
          )}
        </div>
      </div>
      {status && (
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <p className="text-[11px] text-white/80">Subscription status</p>
          <span
            className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium capitalize"
            style={statusStyle(status)}
          >
            <span className="size-1.5 rounded-full bg-current" />
            {status}
          </span>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatusCard;
